import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Truck } from 'lucide-react';
import api from '@/lib/api';
import { getImageUrl } from '@/components/ImageUploadField';

// Redimensionne les URLs images MongoDB (?w=) et Unsplash — sans cela, l'original brut est renvoyé.
const optimizeImg = (url, width) => {
  if (!url) return url;
  if (url.startsWith('/api/')) return getImageUrl(url, width);
  if (url.includes('images.unsplash.com')) {
    const base = url.replace(/&w=\d+/, '').replace(/&q=\d+/, '');
    return `${base}&w=${width}&q=75`;
  }
  return url;
};

function calculateShipping(items, shippingConfig) {
  if (!shippingConfig || !shippingConfig.packages) return null;
  const totalVolume = items.reduce((sum, item) => sum + (item.volume_cm3 || 0) * item.quantity, 0);
  if (totalVolume === 0) return null;
  const sorted = [...shippingConfig.packages]
    .filter(p => p.code !== 'sur_mesure')
    .sort((a, b) => (a.max_volume_cm3 || 0) - (b.max_volume_cm3 || 0));
  for (const pkg of sorted) {
    if (pkg.max_volume_cm3 && totalVolume <= pkg.max_volume_cm3) return { ...pkg, totalVolume };
  }
  const sm = shippingConfig.packages.find(p => p.code === 'sur_mesure');
  return sm ? { ...sm, totalVolume, needsQuote: sm.price_eur === 0 } : null;
}

export default function CartPage() {
  const { items, updateQuantity, removeItem, totalPrice, totalItems } = useCart();
  const [shippingConfig, setShippingConfig] = useState(null);

  useEffect(() => {
    api.get('/shipping-config').then(r => setShippingConfig(r.data)).catch(() => {});
  }, []);

  const shipping = calculateShipping(items, shippingConfig);
  const shippingCost = shipping && !shipping.needsQuote ? shipping.price_eur : 0;
  const grandTotal = totalPrice + shippingCost;

  if (items.length === 0) {
    return (
      <PublicLayout>
        <div className="py-20 px-6 lg:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="w-12 h-12 text-muted-foreground" />
            </div>
            <h1 className="font-heading text-3xl font-semibold mb-4">
              Votre panier est vide
            </h1>
            <p className="text-muted-foreground mb-8">
              Découvrez notre collection de tapis et accessoires de décoration.
            </p>
            <Link to="/commander-en-ligne" data-testid="continue-shopping-empty">
              <Button className="btn-primary">
                Découvrir la collection
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEO
        title="Votre Panier"
        description="Récapitulatif de votre panier Villa Marceau avant de finaliser votre commande."
        canonical="/panier"
        noindex={true}
      />
      <div className="py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold mb-8" data-testid="cart-title">
            Votre Panier ({totalItems} article{totalItems > 1 ? 's' : ''})
          </h1>

          <div className="grid lg:grid-cols-[1fr_400px] gap-12">
            {/* Cart items */}
            <div className="space-y-6">
              {items.map((item) => (
                <div 
                  key={item.id} 
                  className="flex gap-6 p-4 bg-card rounded-lg border border-border"
                  data-testid={`cart-item-${item.id}`}
                >
                  {/* Image */}
                  <Link 
                    to={`/produit/${item.slug}`}
                    className="w-32 h-32 flex-shrink-0 rounded-md overflow-hidden bg-muted"
                  >
                    <img
                      src={optimizeImg(item.images?.[0], 200) || 'https://images.unsplash.com/photo-1726208224138-80303abb73d3?crop=entropy&cs=srgb&fm=jpg&q=85'}
                      alt={item.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </Link>

                  {/* Info */}
                  <div className="flex-1 flex flex-col">
                    <div className="flex-1">
                      <Link 
                        to={`/produit/${item.slug}`}
                        className="font-heading text-lg font-medium hover:text-primary transition-colors"
                      >
                        {item.name}
                      </Link>
                      <p className="text-muted-foreground text-sm mt-1 line-clamp-1">
                        {item.description}
                      </p>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      {/* Quantity controls */}
                      <div className="flex items-center border border-border rounded-full">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="p-2 hover:bg-muted rounded-l-full transition-colors"
                          data-testid={`cart-minus-${item.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-10 text-center text-sm font-medium">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="p-2 hover:bg-muted rounded-r-full transition-colors"
                          data-testid={`cart-plus-${item.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="flex items-center gap-4">
                        <p className="font-semibold text-primary">
                          {(item.price * item.quantity).toFixed(2)} €
                        </p>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                          data-testid={`cart-remove-${item.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order summary */}
            <div className="lg:sticky lg:top-28 lg:self-start">
              <div className="bg-muted/50 rounded-lg p-6 border border-border">
                <h2 className="font-heading text-xl font-semibold mb-6">
                  Récapitulatif
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Sous-total</span>
                    <span>{totalPrice.toFixed(2)} €</span>
                  </div>

                  {shipping ? (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Truck className="w-3 h-3" />
                        {shipping.label}
                      </span>
                      <span className={shipping.needsQuote ? 'text-amber-600 font-medium' : ''}>
                        {shipping.needsQuote ? 'Devis sur demande' : `${shipping.price_eur.toFixed(2)} €`}
                      </span>
                    </div>
                  ) : (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Truck className="w-3 h-3" />
                        Livraison
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {items.some(i => i.volume_cm3) ? 'Calcul en cours...' : 'Renseignez le volume dans les produits'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="border-t border-border pt-4 mb-6">
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Total</span>
                    <span className="text-primary" data-testid="cart-total">
                      {shipping && !shipping.needsQuote ? grandTotal.toFixed(2) : totalPrice.toFixed(2)} €
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    TVA incluse {shipping && !shipping.needsQuote && `• Livraison : ${shippingCost.toFixed(2)} €`}
                  </p>
                </div>


                <Link to="/checkout" data-testid="checkout-btn">
                  <Button className="btn-primary w-full">
                    Commander
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>

                <Link 
                  to="/commander-en-ligne" 
                  className="block text-center text-sm text-muted-foreground hover:text-foreground mt-4 transition-colors"
                  data-testid="continue-shopping"
                >
                  Continuer mes achats
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
